<template>
  <a :href="href" @click="handleClick" class="conv-item" data-cy="conv-item">
    <div v-if="editMode" class="flex align-items-center p-t-xxs" @click.stop>
      <input type="checkbox" :id="`mg_${group.id}`" :data-cy="`conv-checkbox-${group.id}`" :checked="selected"
             @change="$emit('toggle-select', group)" />
    </div>
    <div class="conv-item__avatar"
         :style="{ backgroundColor: avatarColor }">
      <img v-if="otherAvatarUrl"
           :src="otherAvatarUrl"
           :alt="group.name"
           class="conv-item__avatar-img"
           @error="avatarError = true" />
      <i v-else-if="!isPair" class="fa-solid fa-users" aria-hidden="true"></i>
      <template v-else>{{ avatarInitials }}</template>
    </div>
    <div class="conv-item__body">
      <p class="conv-item__name" data-cy="conv-name">{{ group.name }}</p>
      <p class="conv-item__preview" data-cy="conv-preview">{{ previewText }}</p>
    </div>
    <!-- Trailing status column: timestamp on top, status glyphs bottom-right,
         the layout WhatsApp/Telegram use. Keeping the time in here (rather than
         beside the name) is what lets it and the glyphs share an alignment. -->
    <div class="conv-item__meta">
      <span v-if="relativeTime" class="conv-item__time">{{ relativeTime }}</span>
      <div v-if="!editMode" class="conv-item__status">
        <spinner v-if="updating" :inline="true"></spinner>
        <template v-else>
          <i v-if="muted"
             class="fa-solid fa-bell-slash conv-item__muted"
             data-cy="conv-muted"
             aria-label="Muted"
             title="Muted"></i>
          <button v-if="showPin"
                  type="button"
                  class="conv-item__pin-btn"
                  data-cy="conv-pin-btn"
                  :class="{ 'conv-item__pin-btn--active': pinned }"
                  :aria-label="pinned ? 'Unpin conversation' : 'Pin conversation'"
                  @click.prevent.stop="$emit('pin-toggle', group)">
            <i class="fa-solid fa-thumbtack" aria-hidden="true"></i>
          </button>
          <span v-if="hasUnread"
                class="conv-item__pill"
                :class="{ 'conv-item__pill--muted': muted }"
                data-cy="conv-pill">{{ unreadCount }}</span>
        </template>
      </div>
    </div>
  </a>
</template>

<script>
import moment from 'moment';
import Spinner from './spinner.vue';
import { avatarInitials, avatarColor } from '../utilities/avatars';

export default {
  name: 'conversation-list-item',
  components: { Spinner },
  props: {
    group: {
      type: Object,
      required: true,
    },
    profileId: {
      type: Number,
      required: true,
    },
    profileType: {
      type: String,
      required: true,
    },
    editMode: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    hover: {
      type: Boolean,
      default: false,
    },
    updating: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      avatarError: false,
    };
  },
  computed: {
    href() {
      return `/message_groups/${this.group.id}`;
    },
    // A 2-person direct conversation shows the other participant's photo/initials;
    // groups (and 3+ person directs) show a group glyph instead.
    isPair() {
      return this.group.group_type === 'direct' && this.group.member_count === 2;
    },
    otherAvatarUrl() {
      if (!this.isPair || this.avatarError) return null;
      return this.group.other_avatar_url || null;
    },
    membership() {
      return (this.group.memberships || []).find(
        (m) => m.profile.profile_id === this.profileId && m.profile.profile_type === this.profileType,
      );
    },
    unreadCount() {
      return this.membership ? this.membership.unread_count : 0;
    },
    hasUnread() {
      return this.unreadCount > 0;
    },
    pinned() {
      return !!(this.membership && this.membership.pinned);
    },
    muted() {
      return !!(this.membership && this.membership.muted);
    },
    showPin() {
      return this.hover || this.pinned;
    },
    avatarInitials() {
      return avatarInitials(this.group.name);
    },
    avatarColor() {
      return avatarColor(this.group.id);
    },
    previewText() {
      return this.group.teaser && this.group.teaser.length > 0
        ? this.group.teaser
        : 'No messages yet';
    },
    relativeTime() {
      if (!this.group.updated_at) return '';
      const m = moment(this.group.updated_at);
      const now = moment();
      const diffMinutes = now.diff(m, 'minutes');
      if (diffMinutes < 1) return 'now';
      if (diffMinutes < 60) return `${diffMinutes}m`;
      if (now.isSame(m, 'day')) return m.format('h:mma');
      if (now.diff(m, 'days') < 7) return m.format('ddd');
      if (now.isSame(m, 'year')) return m.format('MMM D');
      return m.format('M/D/YY');
    },
  },
  methods: {
    handleClick(event) {
      this.$emit('click', event, this.group);
    },
  },
};
</script>
